import { ReactElement, useMemo } from 'react';
import { FormControlLabel, Grid, Radio, SelectChangeEvent, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormikContext } from 'formik';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import OperatorList from 'civil-status/components/shared/operator-list';
import {
  FilterResearchNotificationsSide,
  FilterResearchNotificationsSideFlags,
} from 'civil-status/view-models/notifications-list-side';
import PmiDivider from 'shell/components/shared/divider';
import ListAnsc from 'civil-status/components/shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';
import { FormControl } from '@mui/material';
import { RadioGroup } from '@mui/material';
import { TypeSearchOperator } from 'civil-status/constants/type-search-operator';
import ListScope from './list-scope';
import ListMunicipalityFormation from './list-municipality-formation';
import ListAnnotationProposals from './list-annotation-proposals';

interface FormFilter {
  idCat?: string;
  formFilterVisible?: FilterResearchNotificationsSideFlags;
  filterKindNotification?: (items: ItemVM[]) => ItemVM[];
  isWithAmbito?: boolean;
}

const defaultVisibleFilters: FilterResearchNotificationsSideFlags = {
  searchOperatore: true,
  codOperator: true,
  register: true,
  dateStart: true,
  dateEnd: true,
  idAnscNotification: true,
  idEventNotification: true,
  idEventNationalNotification: true,
  stateNotification: true,
  typeContentNotification: true,
  kindNotification: true,
  owners: true,
  scope: false,
  municipality: false,
};

const FormFilter = ({
  idCat,
  formFilterVisible = defaultVisibleFilters,
  filterKindNotification,
}: FormFilter): ReactElement => {
  const formContext = useFormikContext<FilterResearchNotificationsSide>();

  const onChangeRegister = useEventCallback((item?: ItemVM) => {
    formContext.setFieldValue('register', item);
  });

  const onChangeScope = useEventCallback((value?: string) => {
    formContext.setFieldValue('scope', value);
  });

  const onChangeMunicipality = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('municipality', event.target.value);
  });

  const onChangeAnnotationProposal = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('annotationProposal', event.target.value);
  });

  const onChangeOperator = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('codOperator', event.target.value);
  });

  const runtimeVisibleFilters = useMemo(() => {
    return {
      ...defaultVisibleFilters,
      ...formFilterVisible,
    };
  }, [formFilterVisible]);

  return (
    <Grid container spacing={2} alignItems="center" display="flex">
      {runtimeVisibleFilters.register ? (
        <Grid item xs={12} md={3}>
          <ListAnsc
            value={formContext.values.register?.id?.toString() || ''}
            error={Boolean(formContext.errors.register) && formContext.touched.register}
            onChange={onChangeRegister}
            disabled={Boolean(idCat)}
            label="Registro di destinazione"
            idDominio="1"
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.scope ? (
        <Grid item xs={12} md={3}>
          <ListScope
            value={formContext.values.scope || ''}
            error={Boolean(formContext.errors.scope) && formContext.touched.scope}
            onChange={onChangeScope}
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.municipality ? (
        <Grid item xs={12} md={3}>
          <ListMunicipalityFormation
            value={formContext.values.municipality || ''}
            error={Boolean(formContext.errors.municipality) && formContext.touched.municipality}
            onChange={onChangeMunicipality}
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.annotationProposal ? (
        <Grid item xs={12} md={3}>
          <ListAnnotationProposals
            value={formContext.values.annotationProposal || ''}
            error={
              Boolean(formContext.errors.annotationProposal) &&
              formContext.touched.annotationProposal
            }
            onChange={onChangeAnnotationProposal}
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.codOperator && runtimeVisibleFilters.searchOperatore ? (
        <Grid item container spacing={2} alignItems="center" display="flex">
          <Grid item xs={12} md={6}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Tipo ricerca operatore"
                id="typePerson"
                value={formContext.values?.searchOperatore || ''}
                onChange={(event) => {
                  formContext.setValues({
                    ...formContext.values,
                    codOperator: '',
                    searchOperatore: event.target.value,
                  });
                }}
              >
                <FormControlLabel
                  value={TypeSearchOperator.NessunFiltroOperatore}
                  control={<Radio />}
                  label="Nessun filtro operatore"
                />
                <FormControlLabel
                  value={TypeSearchOperator.NessunOperatore}
                  control={<Radio />}
                  label="Nessun operatore"
                />
                <FormControlLabel
                  value={TypeSearchOperator.FiltroPerOperatore}
                  control={<Radio />}
                  label="Filtro per operatore"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {formContext.values.searchOperatore === TypeSearchOperator.FiltroPerOperatore && (
            <Grid item xs={12} md={6}>
              <OperatorList
                onChange={onChangeOperator}
                value={formContext.values.codOperator || ''}
                // codiceStatoEvento={formContext.values.stateAct || undefined}
                // disabled={!formContext.values.stateAct}
              />
            </Grid>
          )}
        </Grid>
      ) : null}
      {runtimeVisibleFilters.dateStart ? (
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data inizio"
            aria-label="Data inizio"
            value={formContext.values.dateStart || null}
            onChange={(value) => {
              if (formContext.values.dateEnd) {
                formContext.setFieldValue('dateStart', value);
                return;
              }

              formContext.setValues({
                ...formContext.values,
                dateStart: value,
                dateEnd: value,
              });
            }}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateStart"
                fullWidth
                error={formContext.touched?.dateStart && Boolean(formContext.errors?.dateStart)}
                helperText={formContext.errors?.dateStart}
              />
            )}
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.dateEnd ? (
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data fine"
            aria-label="Data fine"
            value={formContext.values.dateEnd || null}
            onChange={(value) => formContext.setFieldValue('dateEnd', value)}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateEnd"
                fullWidth
                error={formContext.touched?.dateEnd && Boolean(formContext.errors?.dateEnd)}
                helperText={formContext.errors?.dateEnd}
              />
            )}
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <PmiDivider></PmiDivider>
      </Grid>
      {runtimeVisibleFilters.idAnscNotification ? (
        <Grid item xs={12} md={3}>
          <PmiTextField
            label="ID Ansc evento notifica"
            aria-label="idAnsc evento notifica"
            id="idAnscNotification"
            fullWidth
            onChange={(event) => {
              formContext.setFieldValue('idAnscNotification', event.target.value);
            }}
            value={formContext.values?.idAnscNotification || ''}
            helperText={formContext.errors?.idAnscNotification}
            error={
              formContext.touched?.idAnscNotification &&
              Boolean(formContext.errors?.idAnscNotification)
            }
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.idEventNotification ? (
        <Grid item xs={12} md={3}>
          <PmiTextField
            label="ID Ansc evento sorgente"
            aria-label="id Ansc evento sorgente"
            id="idEventNotification"
            fullWidth
            onChange={(event) => {
              formContext.setFieldValue('idEventNotification', event.target.value);
            }}
            value={formContext.values?.idEventNotification || ''}
            helperText={formContext.errors?.idEventNotification}
            error={
              formContext.touched?.idEventNotification &&
              Boolean(formContext.errors?.idEventNotification)
            }
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.idEventNationalNotification ? (
        <Grid item xs={12} md={3}>
          <PmiTextField
            label="ID Nazionale evento sorgente"
            aria-label="id Nazionale evento sorgente"
            id="idEventNationalNotification"
            fullWidth
            onChange={(event) => {
              formContext.setFieldValue('idEventNationalNotification', event.target.value);
            }}
            value={formContext.values?.idEventNationalNotification || ''}
            helperText={formContext.errors?.idEventNationalNotification}
            error={
              formContext.touched?.idEventNationalNotification &&
              Boolean(formContext.errors?.idEventNationalNotification)
            }
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <PmiDivider></PmiDivider>
      </Grid>
      {runtimeVisibleFilters.stateNotification ? (
        <Grid item xs={12} md={3}>
          <ListAnsc
            label="Stato"
            value={formContext.values.stateNotification?.id?.toString() || ''}
            onChange={(el) => formContext.setFieldValue('stateNotification', el)}
            error={
              formContext.touched.stateNotification && Boolean(formContext.errors.stateNotification)
            }
            idDominio="102"
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.typeContentNotification ? (
        <Grid item xs={12} md={3}>
          <ListAnsc
            label="Tipo contenuto notifica"
            value={formContext.values.typeContentNotification?.id?.toString() || ''}
            onChange={(el) => formContext.setFieldValue('typeContentNotification', el)}
            error={
              formContext.touched.typeContentNotification &&
              Boolean(formContext.errors.typeContentNotification)
            }
            idDominio="2"
          />
        </Grid>
      ) : null}

      {runtimeVisibleFilters.kindNotification ? (
        <Grid item xs={12} md={3}>
          <ListAnsc
            label="Genere notifica"
            value={formContext.values.kindNotification?.id?.toString() || ''}
            onChange={(el) => formContext.setFieldValue('kindNotification', el)}
            error={
              formContext.touched.kindNotification && Boolean(formContext.errors.kindNotification)
            }
            idDominio="101"
            filter={filterKindNotification}
          />
        </Grid>
      ) : null}
      {runtimeVisibleFilters.owners ? (
        <Grid item xs={12} md={3}>
          <PmiTextField
            label="Descrizione Intestatario"
            aria-label="Descrizione soggetto intestatario"
            id="owners"
            fullWidth
            onChange={(event) => {
              formContext.setFieldValue('owners', event.target.value);
            }}
            value={formContext.values?.owners || ''}
            helperText={formContext.errors?.owners}
            error={formContext.touched?.owners && Boolean(formContext.errors?.owners)}
          />
        </Grid>
      ) : null}
      <Grid item container spacing={2} xs={12} justifyContent="space-between" display="flex">
        <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
          <Grid item xs={12} md={2}>
            <PmiButton
              variant="outlined"
              color="cdmBrandSecondary"
              fullWidth
              onClick={() => formContext.resetForm()}
            >
              Pulisci
            </PmiButton>
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiButton variant="contained" color="secondary" fullWidth type="submit">
              Cerca
            </PmiButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

FormFilter.displayName =
  'Research notifications - Filters component - AccordionFilterNotifications - Form Filter component';

export default FormFilter;
